import { PAYMENT_STATUS } from "../constants/applicationConstants";
import { fetchInvoiceAgency } from "../redux/slice/invoice/InvoiceSlice";
import { editCommissionRateAction } from "../redux/slice/order/OrderSlice";
import {
  CommissionInfo,
  getPaymentsByInvoice,
  PaymentElementInfo,
} from "../redux/slice/payment/paymentSlice";
import { isAgencyCommissionType, isOverrideCommissionType } from "./Utility";

export type ServiceFeeKind = "AGENCY" | "OVERRIDE";

export type ServiceFeeAmountEditInvoice = {
  invoiceId: number;
  mfgInvoiceNumber?: string | number | null;
  orderNumber?: string | number | null;
  invoiceAmount?: number | string | null;
  agencyId?: number | null;
  overrideAgencyId?: number | null;
};

export type ServiceFeeAmountEditTarget = {
  label: string;
  invoiceId: number;
  columnId: "agencyCommPercent" | "overrideCommPercent";
  editMode: "amount";
  agencyId: number;
  paymentData: {
    paymentId: number;
    invoiceAgencyId: number;
    currentRate: number;
  };
  agencyData: {
    agencyName: string;
    invoiceAmt: number;
    invoiceNumber?: string;
    orderNumber?: string;
  };
};

export type ServiceFeeAmountEditResult =
  | { ok: true; target: ServiceFeeAmountEditTarget }
  | { ok: false; message: string };

const LABEL: Record<ServiceFeeKind, string> = {
  AGENCY: "Edit Agency Service Fee Amount",
  OVERRIDE: "Edit Override Service Fee Amount",
};

const missingDetails = (kind: ServiceFeeKind) =>
  `Unable to open ${LABEL[kind]}: missing agency details for this row.`;

const parseInvoiceAmount = (value: ServiceFeeAmountEditInvoice["invoiceAmount"]) => {
  const numeric = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const parseMoney = (value: unknown) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  if (typeof value === "string") {
    const numeric = Number(value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(numeric) ? numeric : NaN;
  }
  return NaN;
};

const listedPayments = (invoice: { payments?: any } | null | undefined): any[] | null => {
  const payments = invoice?.payments;
  if (Array.isArray(payments)) return payments;
  if (payments && Array.isArray(payments.payments)) return payments.payments;
  return null;
};

/**
 * Amount edits belong to a payment commission. The invoice list does not always
 * include payments, so a fully unpaid invoice (outstanding equals the total) counts as none.
 */
export const invoiceHasServiceFeePayment = (invoice: {
  invoiceStatus?: unknown;
  invoiceAmount?: unknown;
  remainingAmount?: unknown;
  outstandingAmount?: unknown;
  payments?: any;
} | null | undefined): boolean => {
  if (!invoice) return false;
  const payments = listedPayments(invoice);
  if (payments) {
    return payments.some((payment) => {
      const status = String(payment?.paymentStatus || "").toUpperCase();
      return status !== String(PAYMENT_STATUS.VOID) && Number.isFinite(Number(payment?.paymentId));
    });
  }
  const status = String(invoice.invoiceStatus || "").toUpperCase();
  if (status === "PARTIAL" || status === "PAID" || status === "PROCESSING") return true;
  const invoiceAmount = parseMoney(invoice.invoiceAmount);
  const remainingSource =
    invoice.remainingAmount !== undefined && invoice.remainingAmount !== null
      ? invoice.remainingAmount
      : invoice.outstandingAmount;
  const remaining = parseMoney(remainingSource);
  if (!Number.isFinite(invoiceAmount) || !Number.isFinite(remaining)) return false;
  return invoiceAmount - remaining > 0.009;
};

const isKind = (
  allCodeTablesValuesMap: any,
  kind: ServiceFeeKind,
  agencyType: string | null | undefined
) =>
  kind === "AGENCY"
    ? isAgencyCommissionType(allCodeTablesValuesMap, agencyType)
    : isOverrideCommissionType(allCodeTablesValuesMap, agencyType);

/** Prefer the invoice's agency, then an unpaid fee, then the latest match. */
export const pickServiceFeeCommission = (
  payments: PaymentElementInfo[] | undefined,
  kind: ServiceFeeKind,
  preferredAgencyId: number | null | undefined,
  allCodeTablesValuesMap: any
): { payment: PaymentElementInfo; commission: CommissionInfo } | null => {
  const matches = (payments ?? []).flatMap((payment) => {
    if (String(payment?.paymentStatus) === PAYMENT_STATUS.VOID) return [];
    return (payment?.commissions ?? [])
      .filter(
        (commission) =>
          isKind(allCodeTablesValuesMap, kind, commission?.agencyType) &&
          Number.isFinite(Number(payment?.paymentId)) &&
          Number.isFinite(Number(commission?.agencyId)) &&
          Number.isFinite(Number(commission?.invoiceId))
      )
      .map((commission) => ({ payment, commission }));
  });
  if (matches.length === 0) return null;

  const preferredId = Number(preferredAgencyId);
  const preferred = Number.isFinite(preferredId)
    ? matches.filter((match) => Number(match.commission.agencyId) === preferredId)
    : [];
  const pool = preferred.length > 0 ? preferred : matches;
  const unpaid = pool.filter(
    (match) => String(match.commission.commissionStatus || "").toUpperCase() !== "PAID"
  );
  return (unpaid.length > 0 ? unpaid : pool)[ (unpaid.length > 0 ? unpaid : pool).length - 1 ];
};

const unwrapDispatch = async (dispatch: (action: any) => any, action: any) => {
  const result = await dispatch(action);
  if (result && typeof result.unwrap === "function") {
    return result.unwrap();
  }
  return result?.payload ?? result;
};

export const resolveServiceFeeAmountEdit = async (
  dispatch: (action: any) => any,
  allCodeTablesValuesMap: any,
  invoice: ServiceFeeAmountEditInvoice,
  kind: ServiceFeeKind
): Promise<ServiceFeeAmountEditResult> => {
  const label = LABEL[kind];
  try {
    const paymentsResponse = await unwrapDispatch(dispatch, getPaymentsByInvoice(invoice.invoiceId));
    const payments: PaymentElementInfo[] = paymentsResponse?.data?.payments ?? [];
    const preferredAgencyId = kind === "AGENCY" ? invoice.agencyId : invoice.overrideAgencyId;
    const picked = pickServiceFeeCommission(payments, kind, preferredAgencyId, allCodeTablesValuesMap);
    if (!picked) {
      return { ok: false, message: missingDetails(kind) };
    }

    const agencyResponse = await unwrapDispatch(dispatch, fetchInvoiceAgency(invoice.invoiceId));
    const invoiceAgencyList = agencyResponse?.data || [];
    const match = invoiceAgencyList.find(
      (item: any) => Number(item?.agencyId) === Number(picked.commission.agencyId)
    );
    if (!match || !Number.isFinite(Number(match.invoiceAgencyId))) {
      return { ok: false, message: missingDetails(kind) };
    }

    const orderNumber =
      invoice.orderNumber !== undefined && invoice.orderNumber !== null
        ? String(invoice.orderNumber)
        : undefined;

    return {
      ok: true,
      target: {
        label,
        invoiceId: Number(picked.commission.invoiceId),
        columnId: kind === "AGENCY" ? "agencyCommPercent" : "overrideCommPercent",
        editMode: "amount",
        agencyId: Number(picked.commission.agencyId),
        paymentData: {
          paymentId: Number(picked.payment.paymentId),
          invoiceAgencyId: Number(match.invoiceAgencyId),
          currentRate: Number(picked.commission.expectedCommissionRate) || 0,
        },
        agencyData: {
          agencyName: String(picked.commission.agencyName || ""),
          invoiceAmt: parseInvoiceAmount(invoice.invoiceAmount),
          invoiceNumber:
            invoice.mfgInvoiceNumber !== undefined && invoice.mfgInvoiceNumber !== null
              ? String(invoice.mfgInvoiceNumber)
              : undefined,
          orderNumber,
        },
      },
    };
  } catch {
    return { ok: false, message: `Unable to open ${label}.` };
  }
};

export const saveServiceFeeAmountEdit = async (
  dispatch: (action: any) => any,
  data: any,
  label: string
): Promise<{ ok: boolean; unchanged?: boolean; message: string }> => {
  if (data?.noChange) {
    return { ok: true, unchanged: true, message: `${label} unchanged.` };
  }
  try {
    const response: any = await dispatch(editCommissionRateAction(data));
    if (response?.payload?.data?.code === "V3OK") {
      return { ok: true, message: `${label} updated.` };
    }
    const backendMessage =
      response?.payload?.data?.message ||
      response?.payload?.message ||
      response?.payload?.error ||
      (Array.isArray(response?.payload?.errors) ? response.payload.errors.join(", ") : undefined) ||
      response?.error?.message;
    return {
      ok: false,
      message: backendMessage
        ? `Unable to update ${label}: ${backendMessage}`
        : `Unable to update ${label}.`,
    };
  } catch {
    return { ok: false, message: `Unable to update ${label}.` };
  }
};
